import {
    ChatPanel,
    ComposePanel,
    FilterMethod,
    IComment,
    ICustomFilter,
    IMessage,
    ListProviderMessagesOrderEnum,
    MessageLinkEntityType,
    MessageListPanel,
    MessagingProvider,
    ProviderClaim,
    ProviderGuarantor,
    ProviderMessage,
    ProviderMessageSortType,
    ToastContext,
} from '@enter/core';
import { useDocumentView } from '..';
import _ from 'lodash';
import { inject, observer } from 'mobx-react';
import React, { useContext, useEffect, useState } from 'react';
import { DialogContext, IDialogContextProps } from '../../services';
import { IInjectedRootStore, IMessageListingRequest } from '../../stores';
import { MessagingPresenter } from './messaging.presenter';
import * as S from './messaging.styles';

type IMessagingProps = {
    presenter: MessagingPresenter;
    claimId?: string;
    guarantorId?: string;
    messageId?: string;
    onClose: () => void;
    onAssignDocument?: () => void;
    defaultSubject?: string;
    defaultBody?: string;
};

export const ProviderMessaging: React.FunctionComponent<IMessagingProps> = inject('rootStore')(
    observer((props: IMessagingProps) => {
        const injectedProps = props as IInjectedRootStore & IMessagingProps;
        const { rootStore, presenter, claimId, guarantorId } = injectedProps;
        if (!rootStore) {
            return <></>;
        }

        const { roleId } = rootStore;
        const isReadOnly: boolean = roleId === 'PROVIDER_READ_ACCESS';
        const isReadOnlyClaimChoice: boolean = false;

        const dialogContext = useContext(DialogContext) as IDialogContextProps;
        const toastContext = useContext(ToastContext);

        const [isLoading, setLoading] = useState(false);
        const [isResourceLoading, setResourceLoading] = useState(false);
        const [claims, setClaims] = useState<ProviderClaim[]>([]);
        const [messages, setMessages] = useState<ProviderMessage[]>([]);
        const [filter, setFilter] = useState<ICustomFilter>();
        const [isFilterEnabled, setFilterEnabled] = useState(true);
        // const [fetchOptions, setFetchOptions] = useState<ITableFetchOptions>();
        const [openFile] = useDocumentView();

        useEffect(() => {
            (async () => {
                let filterRes: ICustomFilter | undefined;
                if (claimId) {
                    setResourceLoading(true);
                    const claim = await rootStore.claimStore.loadClaim(claimId);
                    setResourceLoading(false);

                    if (claim) {
                        filterRes = generateClaimFilter(claim);
                    }
                }

                if (guarantorId) {
                    setResourceLoading(true);
                    const guarantor = await rootStore.guarantorStore.loadGuarantor(guarantorId);
                    setResourceLoading(false);

                    filterRes = generateGuarantorFilter(guarantor);
                }

                setFilter(filterRes);
                refreshMessageListing(filterRes);
            })();
        }, [claimId, guarantorId]);

        const generateClaimFilter = (claim: ProviderClaim): ICustomFilter => {
            const patientName = _.compact([claim.patientFirst, claim.patientLast]).join(' ');
            return {
                filterType: 'AsyncSelect',
                filterId: 'filterClaimId',
                label: 'Filter Claim',
                filterMethod: FilterMethod.EQ,
                value: {
                    label: patientName,
                    value: claimId,
                },
            };
        };

        const generateGuarantorFilter = (guarantor: ProviderGuarantor): ICustomFilter => {
            return {
                filterType: 'AsyncSelect',
                filterId: 'filterGuarantorId',
                label: 'Filter Guarantor',
                filterMethod: FilterMethod.EQ,
                value: {
                    label: `${guarantor?.first} ${guarantor?.last}`,
                    value: guarantor.id!,
                },
            };
        };

        useEffect(() => {
            let isCancelled = false;

            const loadMeta = async () => {
                try {
                    await Promise.all([
                        rootStore.messageStore.loadMessageCategories(),
                        rootStore.messageStore.loadProviderUsers(),
                    ]);
                } catch (error) {
                    console.error('Failed to load message metadata:', error);
                }
            };

            loadMeta();

            return () => {
                isCancelled = true;
            };
        }, [rootStore.messageStore]);

        // Force MobX to track these observables by accessing them at component level
        const messageStore = rootStore.messageStore;
        const storeInboxMessages = messageStore.inboxMessages;
        const storeInboxTotal = messageStore.inboxTotal;
        const storeDoneMessages = messageStore.doneMessages;
        const storeDoneTotal = messageStore.doneTotal;
        const showTabs = !!claimId;

        const renderChatPanel = (): React.ReactElement => {
            if (presenter.isComposeOpen && !isReadOnly) {
                // const historyId = _.get(history, 'location.state.claimId');
                // const hrefId = getClaimId(window.location.href);
                let subject: string | undefined = _.get(history, 'location.state.subject');
                let body: string | undefined = _.get(history, 'location.state.body');

                if(!subject && props.defaultSubject) {
                    subject = props.defaultSubject;
                }

                if(!body && props.defaultBody) {
                    body = props.defaultBody;
                }

                return (
                    <S.ChatPanelContainer>
                        <ComposePanel
                            claimId={claimId}
                            subject={subject}
                            body={body}
                            forceClaim={!!claimId}
                            hideNote={true}
                            enableProviderNote={true}
                        />
                    </S.ChatPanelContainer>
                );
            }

            if (presenter.messageDetails) {
                return (
                    <S.ChatPanelContainer>
                        <ChatPanel onBack={handleCloseMessageDetails} hideNote={true} />
                    </S.ChatPanelContainer>
                );
            }
            
            return (
                <S.ChatPanelContainer>
                    <MessageListPanel
                        inboxCount={storeInboxTotal}
                        inboxMessages={storeInboxMessages.map(m => ({
                            ...m,
                            comments: m.commentCount ?? (Array.isArray(m.comments) ? m.comments.length : 0),
                            documents: m.documentCount ?? 0,
                            claim: m.links?.claims?.[0]?.id,
                        }))}
                        doneCount={showTabs ? storeDoneTotal : undefined}
                        doneMessages={showTabs ? storeDoneMessages.map(m => ({
                            ...m,
                            comments: m.commentCount ?? (Array.isArray(m.comments) ? m.comments.length : 0),
                            documents: m.documentCount ?? 0,
                            claim: m.links?.claims?.[0]?.id,
                        })) : undefined}
                        showInboxDoneTabs={showTabs}
                        onFetchData={async (newTableState) => {
                            if (claimId) {
                                // Skip only the initial fetch without claim filter - refreshMessageListing handles initial load
                                // Allow subsequent fetches (like switching tabs) even if filter prop isn't set yet
                                const hasClaimFilterInProps = !!filter;
                                const hasClaimFilterInValidFilters = _.some(newTableState.validFilters, { filterId: 'filterClaimId' });
                                const hasDoneFilter = _.some(newTableState.validFilters, f => 
                                    f.filterId === 'filterOpen' && (f.value === '0' || (f.value as any)?.value === '0')
                                );
                                
                                // Only skip if: no claim filter anywhere AND no done filter (meaning it's the initial inbox fetch)
                                if (!hasClaimFilterInProps && !hasClaimFilterInValidFilters && !hasDoneFilter) {
                                    return;
                                }
                                
                                setLoading(true);
                                try {
                                    const baseReq = await buildRequest(filter);
                                    const baseFilters = baseReq.filters || [];
                                    const doneFilterInRequest = _.find(newTableState.validFilters || [], filter => {
                                        if (filter.filterId === 'filterOpen') {
                                            const filterValue = typeof filter.value === 'string' ? filter.value : (filter.value as any)?.value;
                                            return filterValue === '0'; // Check for '0' (done)
                                        }
                                        return false;
                                    });
                                    
                                    if (doneFilterInRequest) {
                                        // Loading done messages (filterOpen=EQ:0)
                                        await rootStore.messageStore.loadMessageListing({
                                            ...baseReq,
                                            page: newTableState.pageIndex || 0,
                                            pageSize: newTableState.pageSize || 100,
                                            filters: [...baseFilters, ...(newTableState.validFilters || [])],
                                        });
                                    } else {
                                        // Loading inbox messages (default, filterOpen=EQ:1)
                                        const filtersWithoutOpen = (newTableState.validFilters || []).filter(f => f.filterId !== 'filterOpen');
                                        const inboxFilter: ICustomFilter = {
                                            filterId: 'filterOpen',
                                            filterMethod: FilterMethod.EQ,
                                            filterType: 'String',
                                            value: '1', // Inbox (open = 1)
                                        };
                                        await rootStore.messageStore.loadMessageListing({
                                            ...baseReq,
                                            page: newTableState.pageIndex || 0,
                                            pageSize: newTableState.pageSize || 100,
                                            filters: [...baseFilters, ...filtersWithoutOpen, inboxFilter],
                                        });
                                    }
                                } finally {
                                    setLoading(false);
                                }
                            }
                        }}
                        onShowCompose={handleShowCompose}
                        selectedMessageId={undefined}
                        filter={isFilterEnabled ? filter : undefined}
                        onFilterRemove={handleFilterRemove}
                    />
                </S.ChatPanelContainer>
            );
        };

        const refreshMessageListing = async (filter?: ICustomFilter): Promise<void> => {
            setLoading(true);

            // Only load inbox messages when on claim page - done messages will be loaded on-demand when Done tab is clicked
            if (claimId) {
                const inboxFilter: ICustomFilter = {
                    filterId: 'filterOpen',
                    filterMethod: FilterMethod.EQ,
                    filterType: 'String',
                    value: '1', // Inbox (open = 1)
                };

                const baseReq = await buildRequest(filter);
                const baseFilters = baseReq.filters || [];
                
                // Only load inbox messages upfront - done messages will be loaded when Done tab is clicked
                await rootStore.messageStore.loadMessageListing({
                    ...baseReq,
                    filters: [...baseFilters, inboxFilter], // Inbox (open = 1)
                });

                // Keep the sorted messages for backward compatibility
                const allMessages = [...rootStore.messageStore.inboxMessages, ...rootStore.messageStore.doneMessages];
                const sortedMessages = _.sortBy(allMessages, 'open').reverse();
                setMessages(sortedMessages);
            } else {
                // For non-claim pages, add filterOpen so messages go to inboxMessages (not doneMessages)
                const inboxFilter: ICustomFilter = {
                    filterId: 'filterOpen',
                    filterMethod: FilterMethod.EQ,
                    filterType: 'String',
                    value: '1',
                };
                const inboxReq = await buildRequest(filter);
                const messages = await rootStore.messageStore.loadMessageListing({
                    ...inboxReq,
                    filters: [...(inboxReq.filters || []), inboxFilter],
                });
                setMessages(messages.messages.reverse());
            }
            setLoading(false);
        };

        const refreshMessageDetail = async (mid: string): Promise<void> => {
            await presenter.openMessageById(mid);
        };

        const refresh = async (ids: string | string[]): Promise<void> => {
            // multiple IDs from selection in message-list?
            if (_.isArray(ids)) {
                await refreshMessageListing();
            } else {
                await refreshMessageDetail(ids);
            }
        };

        const handleShowCompose = (): void => {
            presenter.setComposeOpen(true);
        };

        const buildRequest = async (filter?: ICustomFilter): Promise<IMessageListingRequest> => {
            const sort: ProviderMessageSortType[] = [ProviderMessageSortType.UPDATED];
            const order: ListProviderMessagesOrderEnum[] = [ListProviderMessagesOrderEnum.DESCENDING];

            const claimFilter = filter?.filterId === 'filterClaimId' ? filter : undefined;
            const guarantorFilter = filter?.filterId === 'filterGuarantorId' ? filter : undefined;

            const filters = [claimFilter, guarantorFilter];

            // if (guarantorFilter?.value) {
            //     const id = (guarantorFilter?.value as ISelectOption<string>).value;
            //     if (id) {
            //         const claims = await rootStore.claimStore.getGuarantorClaims(id);
            //         if (claims && claims.length > 0) {
            //             claims.forEach(c => {
            //                 filters.push({
            //                     filterType: 'AsyncSelect',
            //                     filterId: 'filterClaimId',
            //                     filterMethod: FilterMethod.EQ,
            //                     value: {
            //                         label: c.id || 'Claim',
            //                         value: c.id,
            //                     },
            //                 });
            //             });
            //         }
            //     }
            // }

            return {
                page: 0,
                pageSize: 100,
                sort: sort as any,
                order: order as any,
                filters: _.compact(filters),
            };
        };

        const handleCloseMessageDetails = (): void => {
            presenter.closeDetails();
        };

        const claimSearch = async (term: string): Promise<ProviderClaim[]> => {
            console.log("components claimSearch");
            return await doSearch(term);
        };

        // Create a debounced function
        const debouncedDoSearch = _.debounce(async (term, resolve, reject) => {
            try {
                setClaims([]);
                setLoading(true);
                const results = await rootStore.claimStore.loadBasicClaimSearchResults(term);
                setClaims(results);
                setLoading(false);
                resolve(results);  // Resolve the promise with the results
            } catch (error) {
                reject(error);  // Reject the promise if there's an error
            }
        }, 800);

        // Wrapper function that returns a Promise
        const doSearch = async (term: string): Promise<ProviderClaim[]> => {
            return new Promise((resolve, reject) => {
                debouncedDoSearch(term, resolve, reject);
            });
        };

        const composeComment = async ({ messageId: mid, body, files }: IComment): Promise<void> => {
            try {
                await rootStore.messageStore.addComment(rootStore.account, mid, body, files);
                await refreshMessageDetail(mid);
            } catch (err) {
                dialogContext.showError(err);
            }
        };

        const composeMessage = async ({
            subject,
            body,
            isPrivateNote,
            claimId,
            files,
            links,
            priority,
            dueDate,
        }: IMessage): Promise<void | string> => {
            try {
                // Extract claim IDs from links if available, otherwise use single claimId
                const claimIds = links?.claims?.map(c => c.id).filter(Boolean) || (claimId ? [claimId] : []);
                // Extract category IDs from links if available
                const categoryIds = links?.messageCategories?.map(c => c.id).filter(Boolean);
                // Extract user IDs from links if available
                const assignedUserIds = links?.users?.map(u => u.id).filter(Boolean);
                const messageId = await rootStore.messageStore.composeMessage(
                    rootStore.account,
                    subject,
                    body,
                    claimIds,
                    files,
                    priority,
                    dueDate,
                    categoryIds,
                    assignedUserIds,
                    isPrivateNote,
                );
                await refresh([]);
                toastContext.notify?.('Message sent', 'right');
                return messageId;
            } catch (err) {
                dialogContext.showError(err);
            }
        };

        const downloadDocument = async (id: string): Promise<void> => {
            const file = await rootStore.messageStore.embed(id);

            file.url && openFile(file.url);
        };

        const actualDownloadDocument = async (id: string): Promise<string> => {
            const file = await rootStore.messageStore.download(id);
            return file?.url || '';
        };

        const messageOpen = async (idOrMessage: string | ProviderMessage): Promise<void> => {
            let message: ProviderMessage;
            
            if (typeof idOrMessage === 'string') {
                // Find message from store
                const foundMessage = rootStore.messageStore.findMessage(idOrMessage);
                if (!foundMessage) {
                    console.warn(`[messaging.messageOpen] Message not found in store: ${idOrMessage}`);
                    return;
                }
                message = foundMessage;
            } else {
                message = idOrMessage;
            }
            
            // The panel should already be open since user clicked within it,
            // but just in case, ensure isOpen is true so the message detail renders
            if (!presenter.isOpen) {
                presenter.setOpen(true);
            }
            
            await presenter.openMessage(message);

            // mark read without waiting
            rootStore.messageStore.markAsRead(message.id!);
        };

        const messageClose = async (): Promise<void> => {
            presenter.setComposeOpen(false);
            props.onClose();
        };

        const markAsOpen = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsOpen(id),
            );
            await Promise.all(promises);
            const updatedMessages = messages.map(message => {
                if (ids.includes(message.id!)) {
                    message.open = true;
                }

                return message;
            });
            setMessages(updatedMessages);
            await refresh(ids);
        };

        const markAsClosed = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsClosed(id),
            );
            await Promise.all(promises);
            const updatedMessages = messages.map(message => {
                if (ids.includes(message.id!)) {
                    message.open = false;
                }

                return message;
            });
            setMessages(updatedMessages);

            await refresh(ids);
        };

        const markAsRead = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsRead(id),
            );
            await Promise.all(promises);
            const updatedMessages = messages.map(message => {
                if (ids.includes(message.id!)) {
                    message.read = true;
                }

                return message;
            });
            setMessages(updatedMessages);

            await refresh(ids);
        };

        const markAsUnread = async (ids: string | string[]): Promise<void> => {
            const promises = _.map(
                _.concat([], ids),
                async (id): Promise<void> => rootStore.messageStore.markAsUnread(id),
            );
            await Promise.all(promises);
            const updatedMessages = messages.map(message => {
                if (ids.includes(message.id!)) {
                    message.read = false;
                }

                return message;
            });
            setMessages(updatedMessages);

            await refresh(ids);
        };

        const handleFilterRemove = () => {
            setFilterEnabled(false);
            refreshMessageListing();
        };

        const linkEntityToMessage = async (id: string, entityType: MessageLinkEntityType, entityIds: string[]): Promise<void> => {
            // Clear search results so ClaimCard fetches all linked claims from API
            setClaims([]);
            await presenter.linkEntityToMessage(id, entityType, entityIds);
        };

        const unlinkEntityFromMessage = async (id: string, entityType: MessageLinkEntityType, entityIds: string[]): Promise<void> => {
            // Clear search results so ClaimCard fetches all linked claims from API
            setClaims([]);
            await presenter.unlinkEntityFromMessage(id, entityType, entityIds);
        };

        return (
            <MessagingProvider
                isReadOnlyClaimChoice={isReadOnlyClaimChoice}
                isReadOnly={isReadOnly}
                isListLoading={isLoading || presenter.isDetailLoading || isResourceLoading}
                isDetailLoading={presenter.isDetailLoading}
                account={rootStore.account}
                enableMessageTypeValidation={true}
                message={presenter.messageDetails ? presenter.messageDetails.message : undefined}
                comments={presenter.messageDetails ? presenter.messageDetails.comments : undefined}
                documents={presenter.messageDetails ? presenter.messageDetails.documents : undefined}
                claims={claims}
                getCategoryName={rootStore.messageStore.getCategoryName}
                getUserName={rootStore.messageStore.getUserName}
                onClaimSearch={claimSearch}
                onComposeComment={composeComment}
                onComposeMessage={composeMessage}
                onDocumentDownload={downloadDocument}
                onDocumentActualDownload={actualDownloadDocument}
                onMarkAsClosed={markAsClosed}
                onMarkAsOpen={markAsOpen}
                onMarkAsRead={markAsRead}
                onMarkAsUnread={markAsUnread}
                onMessageOpen={messageOpen}
                onMessageClose={messageClose}
                onLinkEntityToMessage={linkEntityToMessage}
                onUnlinkEntityFromMessage={unlinkEntityFromMessage}>
                <>{presenter.isOpen && renderChatPanel()}</>
            </MessagingProvider>
        );
    }),
);
